import type { AdminSettlementResponse } from '@inflip/contracts/admin';
import { SettlementStatus } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DataTable } from '../components/data-table';
import { DateRangeFilter } from '../components/date-range-filter';
import { TerminationBadge } from '../components/termination-badge';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pager,
  PromptDialog,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { SETTLEMENT_STATUS_META } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

type DialogState =
  | null
  | { kind: 'hold'; settlement: AdminSettlementResponse }
  | { kind: 'release'; settlement: AdminSettlementResponse }
  | { kind: 'payout'; settlement: AdminSettlementResponse };

export function SettlementsPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<SettlementStatus | ''>('status', '');
  // 기간 — 정산 예정일 기준. 기본은 전체 (처리할 건이 기간 밖으로 숨지 않게)
  const [dateFrom, setDateFrom] = useListViewState('from', '');
  const [dateTo, setDateTo] = useListViewState('to', '');
  const [page, setPage] = useListViewState('page', 1);
  const [rows, setRows] = useState<AdminSettlementResponse[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .listSettlements(session.accessToken, {
        ...(filter ? { status: filter } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        page,
      })
      .then((r) => {
        setRows(r.items);
        setTotalPages(r.totalPages);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, filter, dateFrom, dateTo, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 다른 어드민·회원이 상태를 바꿔 거절됐을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  const token = session?.accessToken;

  const columns = useMemo<ColumnDef<AdminSettlementResponse>[]>(
    () => [
      {
        id: 'status',
        header: '상태',
        size: 110,
        cell: ({ row }) => (
          <span className={`text-xs font-bold ${SETTLEMENT_STATUS_META[row.original.status].cls}`}>
            {SETTLEMENT_STATUS_META[row.original.status].label}
          </span>
        ),
      },
      {
        id: 'order',
        header: '주문',
        cell: ({ row }) => (
          <Link
            to="/orders/$orderId"
            params={{ orderId: row.original.orderId }}
            className="font-mono text-xs font-medium text-brand hover:underline"
          >
            주문 {row.original.orderId.slice(0, 8)}
          </Link>
        ),
      },
      {
        id: 'seller',
        header: '판매자',
        meta: { tdClass: 'text-xs' },
        cell: ({ row }) => {
          const s = row.original;
          const closed =
            s.status === SettlementStatus.PAID_OUT || s.status === SettlementStatus.CANCELLED;
          return (
            <>
              <div className="font-medium">
                {s.sellerName || `판매자 ${s.sellerId.slice(0, 8)}`}
              </div>
              {s.sellerPayoutAccount ? (
                <div className="text-[11px] text-muted-foreground">
                  {s.sellerPayoutAccount.bankName} {s.sellerPayoutAccount.accountNumber} ·{' '}
                  {s.sellerPayoutAccount.holderName}
                </div>
              ) : (
                // 지급이 끝난 건은 경고할 이유가 없다
                <div
                  className={`text-[11px] font-bold ${closed ? 'text-muted-foreground' : 'text-red-600'}`}
                >
                  계좌 미등록
                </div>
              )}
            </>
          );
        },
      },
      {
        id: 'amount',
        header: '금액',
        meta: { align: 'right', tdClass: 'tabular-nums' },
        cell: ({ row }) => (
          <>
            <div className="font-bold">{row.original.netAmount.toLocaleString('ko-KR')}원</div>
            <div className="text-[11px] text-muted-foreground">
              총 {row.original.grossAmount.toLocaleString('ko-KR')} − 수수료{' '}
              {row.original.feeAmount.toLocaleString('ko-KR')}
            </div>
          </>
        ),
      },
      {
        id: 'schedule',
        header: '일정',
        meta: { tdClass: 'text-xs text-muted-foreground' },
        cell: ({ row }) => (
          <>
            예정 {formatDateTime(row.original.scheduledAt)}
            {row.original.paidOutAt && <div>지급 {formatDateTime(row.original.paidOutAt)}</div>}
          </>
        ),
      },
      {
        id: 'actions',
        header: '액션',
        minSize: 160,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const s = row.original;
          if (s.status === SettlementStatus.PAID_OUT || s.status === SettlementStatus.CANCELLED)
            return null;
          // 보류 중에는 지급할 수 없다 — 해제 후 지급
          if (s.status === SettlementStatus.ON_HOLD)
            return (
              <div className="flex justify-end gap-1.5">
                <Button
                  size="sm"
                  disabled={pending}
                  onClick={() => setDialog({ kind: 'release', settlement: s })}
                >
                  보류 해제
                </Button>
              </div>
            );
          return (
            <div className="flex items-center justify-end gap-1.5">
              {/* 분쟁 중이면 지급하지 않는다 — 주문에서 분쟁을 먼저 해결 */}
              {s.orderTermination && (
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: s.orderId }}
                  className="mr-1 inline-flex items-center gap-1 text-xs font-bold text-rose-600 hover:underline"
                >
                  계약 해지
                  <TerminationBadge termination={s.orderTermination} />
                </Link>
              )}
              {s.orderDisputed && (
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: s.orderId }}
                  className="mr-1 text-xs font-bold text-rose-600 hover:underline"
                >
                  분쟁 중
                </Link>
              )}
              <Button
                size="sm"
                variant="danger"
                disabled={pending}
                onClick={() => setDialog({ kind: 'hold', settlement: s })}
              >
                보류
              </Button>
              <Button
                size="sm"
                title={s.orderDisputed ? '분쟁 중인 주문은 지급할 수 없어요' : undefined}
                disabled={pending || !s.sellerPayoutAccount || s.orderDisputed}
                onClick={() => setDialog({ kind: 'payout', settlement: s })}
              >
                지급 완료
              </Button>
            </div>
          );
        },
      },
    ],
    [pending],
  );

  if (!session || !token) return null;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="정산 관리"
        description="판매자 정산의 보류·지급을 처리해요 · 기간은 정산 예정일 기준"
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <Chip
          active={filter === ''}
          onClick={() => {
            setPage(1);
            setFilter('');
          }}
        >
          전체
        </Chip>
        {Object.entries(SETTLEMENT_STATUS_META).map(([k, v]) => (
          <Chip
            key={k}
            active={filter === k}
            onClick={() => {
              setPage(1);
              setFilter(k as SettlementStatus);
            }}
          >
            {v.label}
          </Chip>
        ))}
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(from, to) => {
            setPage(1);
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>

      {error && <ErrorBanner message={error} />}

      {rows.length === 0 ? (
        <EmptyState message="정산 건이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(s) => s.id} />
      )}
      <Pager page={page} totalPages={totalPages} onChange={setPage} />

      {dialog?.kind === 'hold' && (
        <PromptDialog
          title="정산을 보류할까요?"
          description="보류 사유는 감사 로그에 남아요."
          placeholder="보류 사유"
          submitLabel="보류"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.holdSettlement(token, dialog.settlement.id, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'release' && (
        <ConfirmDialog
          title="보류를 해제할까요?"
          description="해제하면 다시 지급 대기 상태가 돼요."
          confirmLabel="보류 해제"
          busy={pending}
          onConfirm={() => void run(() => api.releaseSettlement(token, dialog.settlement.id))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'payout' && (
        <ConfirmDialog
          title="지급 완료 처리할까요?"
          description={`${dialog.settlement.netAmount.toLocaleString('ko-KR')}원 — 실제 송금을 마친 뒤 눌러주세요.`}
          confirmLabel="지급 완료"
          busy={pending}
          onConfirm={() => void run(() => api.payOutSettlement(token, dialog.settlement.id))}
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}
